import React, { useState, Fragment, useEffect } from 'react';
import { getFirestore, doc, getDoc, collection, addDoc, updateDoc, arrayUnion, setDoc, query, where, getDocs } from 'firebase/firestore';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, PlusCircleIcon } from '@heroicons/react/24/outline';
import { DocumentTextIcon, SparklesIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/solid';
import { toast } from 'react-hot-toast';
import { getFunctions, httpsCallable } from 'firebase/functions';
import AddCompanyModal from './AddCompanyModal';
import { Combobox } from '@headlessui/react';

const AddJobModal = ({ isOpen, onClose, user, userId, onJobAdded }) => {
  // Use user from props, or fall back to getting user ID from userId
  const currentUser = user || (userId ? { uid: userId } : null);
  
  const [jobFormData, setJobFormData] = useState({
    title: '',
    company: '',
    companyId: '', // Add companyId field to store the selected company's ID
    location: '',
    description: '',
    responsibilities: '',
    requirements: '',
    experienceLevel: 'mid',
    salary: '',
    industry: '',
    roleType: 'technical',
    workEnvironment: '', // Changed from 'onsite' to empty string
    workplace: 'remote', // Added separate workplace field with default value
    applicationUrl: '',
    applicationDeadline: '',
    postedDate: '',
    summary: '',
    status: 'active'
  });
  const [formErrors, setFormErrors] = useState({});
  const [isFetchingDetails, setIsFetchingDetails] = useState(false);
  const [fetchError, setFetchError] = useState('');
  const [showJobDetails, setShowJobDetails] = useState(false);
  
  // Company search and selection states
  const [companySearchQuery, setCompanySearchQuery] = useState('');
  const [companies, setCompanies] = useState([]);
  const [filteredCompanies, setFilteredCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [isAddCompanyModalOpen, setIsAddCompanyModalOpen] = useState(false);
  
  // Fetch companies on component mount
  useEffect(() => {
    fetchCompanies();
  }, []);
  
  // Filter companies when search query changes
  useEffect(() => {
    if (companySearchQuery === '') {
      setFilteredCompanies(companies);
    } else {
      const filtered = companies.filter(company => 
        company.name.toLowerCase().includes(companySearchQuery.toLowerCase()) ||
        (company.industry && company.industry.toLowerCase().includes(companySearchQuery.toLowerCase()))
      );
      setFilteredCompanies(filtered);
    }
  }, [companySearchQuery, companies]);
  
  // Fetch companies from Firestore
  const fetchCompanies = async () => {
    try {
      // Skip fetching companies if we're on the landing page or employer page
      // This prevents Firebase permission errors when not logged in
      if (window.location.pathname === '/' || 
          window.location.pathname === '/employers' ||
          window.location.pathname === '/login') {
        setCompanies([]);
        setFilteredCompanies([]);
        return;
      }

      const db = getFirestore();
      const companiesCollection = collection(db, 'companies');
      
      try {
        const companiesSnapshot = await getDocs(companiesCollection);
        
        const companiesList = companiesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        setCompanies(companiesList);
        setFilteredCompanies(companiesList);
      } catch (firestoreError) {
        // Silently handle permission errors
        console.log('Note: Companies could not be loaded. This is expected on public pages.');
        setCompanies([]);
        setFilteredCompanies([]);
      }
    } catch (error) {
      // Silently handle errors on public pages
      console.log('Note: Companies fetch was skipped.');
      setCompanies([]);
      setFilteredCompanies([]);
    }
  };
  
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setJobFormData({
      ...jobFormData,
      [name]: value
    });
    
    // Clear error for this field if it exists
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: ''
      });
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!jobFormData.title.trim()) {
      errors.title = 'Job title is required';
    }
    
    if (!jobFormData.company.trim()) {
      errors.company = 'Company name is required';
    }
    
    if (!jobFormData.applicationUrl.trim()) {
      errors.applicationUrl = 'Application URL is required';
    } else if (!/^https?:\/\/.+/i.test(jobFormData.applicationUrl)) {
      errors.applicationUrl = 'Please enter a valid URL starting with http:// or https://';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleFetchJobDetails = async () => {
    // Validate URL first
    if (!jobFormData.applicationUrl.trim()) {
      setFetchError('Please enter a job URL first');
      toast.error('Please enter a job URL first');
      return;
    }
    
    if (!/^https?:\/\/.+/i.test(jobFormData.applicationUrl)) {
      setFetchError('Please enter a valid URL starting with http:// or https://');
      toast.error('Please enter a valid URL starting with http:// or https://');
      return;
    }
    
    setIsFetchingDetails(true);
    setFetchError('');
    
    try {
      const functions = getFunctions();
      const extractJobDetails = httpsCallable(functions, 'extractJobDetails');
      
      const result = await extractJobDetails({ jobUrl: jobFormData.applicationUrl });
      const jobDetails = result.data;
      
      console.log('Extracted job details:', jobDetails);
      
      // Update form with extracted data
      const updatedFormData = {
        ...jobFormData,
        title: jobDetails.title || jobFormData.title,
        company: jobDetails.company || jobFormData.company,
        location: jobDetails.location || jobFormData.location,
        description: jobDetails.description || jobFormData.description,
        // Handle both array and string types for responsibilities and requirements
        responsibilities: typeof jobDetails.responsibilities === 'string' 
          ? jobDetails.responsibilities 
          : Array.isArray(jobDetails.responsibilities) 
            ? jobDetails.responsibilities.join('\n') 
            : jobFormData.responsibilities,
        requirements: typeof jobDetails.requirements === 'string'
          ? jobDetails.requirements
          : Array.isArray(jobDetails.requirements)
            ? jobDetails.requirements.join('\n')
            : jobFormData.requirements,
        experienceLevel: jobDetails.experienceLevel || jobFormData.experienceLevel,
        salary: jobDetails.salary || jobFormData.salary,
        industry: jobDetails.industry || jobFormData.industry,
        roleType: jobDetails.roleType || jobFormData.roleType,
        workEnvironment: jobDetails.workEnvironment || jobFormData.workEnvironment,
        // Map locationType to standardized workplace values
        workplace: jobDetails.locationType === 'remote' ? 'remote' :
                   jobDetails.locationType === 'hybrid' ? 'hybrid' :
                   jobDetails.locationType === 'onsite' ? 'on-site' :
                   jobFormData.workplace,
        applicationDeadline: jobDetails.applicationDeadline || jobFormData.applicationDeadline,
        postedDate: jobDetails.postedDate || jobFormData.postedDate,
        summary: jobDetails.summary || jobFormData.summary
      };
      
      setJobFormData(updatedFormData);
      toast.success('Job details extracted successfully!');
    } catch (error) {
      console.error('Error extracting job details:', error);
      setFetchError('Failed to extract job details. Please try again or fill in the form manually.');
      toast.error('Failed to extract job details. Please try again or fill in the form manually.');
    } finally {
      setIsFetchingDetails(false);
    }
  };

  const handleAddJob = async () => {
    if (!validateForm()) {
      toast.error('Please fix the errors in the form');
      return;
    }
    
    try {
      const db = getFirestore();
      
      // Format the job data
      const jobData = {
        title: jobFormData.title,
        company: jobFormData.company,
        companyId: jobFormData.companyId || '', // Include the company ID if present
        location: jobFormData.location || '',
        description: jobFormData.description || '',
        responsibilities: jobFormData.responsibilities || '',
        requirements: jobFormData.requirements || '',
        experienceLevel: jobFormData.experienceLevel || 'mid',
        salary: jobFormData.salary || '',
        industry: jobFormData.industry || '',
        roleType: jobFormData.roleType || 'technical',
        workEnvironment: jobFormData.workEnvironment || '', // Description of work environment
        workplace: jobFormData.workplace || 'remote', // Type of workplace (remote/hybrid/on-site)
        locationType: jobFormData.workplace || 'remote', // For compatibility with existing code
        applicationUrl: jobFormData.applicationUrl,
        applicationDeadline: jobFormData.applicationDeadline || '',
        postedDate: jobFormData.postedDate || '',
        summary: jobFormData.summary || '',
        status: 'active'
      };
      
      // Add the job to Firestore jobs collection
      const jobsCollectionRef = collection(db, 'jobs');
      const jobRef = await addDoc(jobsCollectionRef, jobData);
      const jobId = jobRef.id;
      
      // Update the user's profile in user_profiles collection
      const userProfileRef = doc(db, 'user_profiles', currentUser.uid);
      const userProfileSnap = await getDoc(userProfileRef);
      
      const timestamp = new Date().toISOString();
      
      if (userProfileSnap.exists()) {
        // Update existing profile
        // 1. Add to savedJobs array
        await updateDoc(userProfileRef, {
          savedJobs: arrayUnion(jobId),
          updatedAt: new Date().toISOString()
        });
        
        // 2. Set job status to "first-look"
        await updateDoc(userProfileRef, {
          [`jobStatuses.${jobId}`]: "first-look"
        });
        
        // 3. Add to job status history
        await updateDoc(userProfileRef, {
          [`jobStatusHistory.${jobId}`]: arrayUnion({
            status: "first-look",
            previousStatus: "first-look",
            timestamp: timestamp
          })
        });
      } else {
        // Create new user profile with this job
        await setDoc(userProfileRef, {
          savedJobs: [jobId],
          jobStatuses: {
            [jobId]: "first-look"
          },
          jobStatusHistory: {
            [jobId]: [{
              status: "first-look",
              previousStatus: "first-look",
              timestamp: timestamp
            }]
          },
          email: currentUser.email,
          displayName: currentUser.displayName || 'Anonymous User',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          userId: currentUser.uid,
          uid: currentUser.uid
        });
      }
      
      toast.success('Job added successfully!');
      
      // If onJobAdded callback exists, pass the job ID, job data, and options
      if (onJobAdded) {
        // Include the complete job data with ID for immediate rendering
        const completeJobData = {
          ...jobData,
          id: jobId
        };
        
        // Pass options to switch to kanban view and force a refresh
        onJobAdded(jobId, { 
          view: 'kanban', 
          refresh: true,
          jobData: completeJobData 
        });
      }
      
      onClose();
    } catch (error) {
      console.error('Error adding job:', error);
      toast.error('Failed to add job. Please try again.');
    }
  };
  
  // Handle selecting a company from the dropdown
  const handleSelectCompany = (company) => {
    setSelectedCompany(company);
    setJobFormData({
      ...jobFormData,
      company: company.name,
      companyId: company.id,
      industry: company.industry || jobFormData.industry
    });
  };
  
  // Handle adding a new company
  const handleAddNewCompany = (newCompany) => {
    // Add the new company to the companies list
    setCompanies(prevCompanies => [...prevCompanies, newCompany]);
    
    // Set the new company as the selected company
    setSelectedCompany(newCompany);
    setJobFormData({
      ...jobFormData,
      company: newCompany.name,
      companyId: newCompany.id,
      industry: newCompany.industry || jobFormData.industry
    });
    
    // Close the Add Company modal
    setIsAddCompanyModalOpen(false);
    
    // Show success message
    toast.success('Company added successfully!');
  };

  return (
    <>
      <Transition.Root show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={onClose}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-4xl">
                  <div className="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
                    <button
                      type="button"
                      className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                      onClick={onClose}
                    >
                      <span className="sr-only">Close</span>
                      <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                    </button>
                  </div>
                  
                  <div className="bg-white px-6 py-6">
                    <div className="border-b border-gray-200 pb-4">
                      <Dialog.Title as="h3" className="text-xl font-semibold leading-6 text-gray-900">
                        Add New Job
                      </Dialog.Title>
                      <p className="mt-1 text-sm text-gray-500">
                        Enter a job posting URL and let AI extract the details, or fill in the form manually.
                      </p>
                    </div>
                    
                    <div className="mt-4 space-y-4">
                      {/* URL and AI Extraction Section */}
                      <div className="bg-purple-50 p-4 rounded-md border border-purple-100">
                        <h4 className="text-base font-medium text-purple-800 mb-2">
                          Job Post URL (Link to scrape)
                        </h4>
                        
                        <div className="flex items-end gap-2">
                          <div className="flex-grow">
                            <input
                              type="text"
                              name="applicationUrl"
                              id="applicationUrl"
                              value={jobFormData.applicationUrl}
                              onChange={handleFormChange}
                              className={`block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm ${
                                formErrors.applicationUrl ? 'border-red-500' : ''
                              }`}
                              placeholder="https://example.com/job-posting"
                            />
                            <p className="mt-1 text-xs text-purple-600">
                              Enter the URL of the job posting to automatically extract details.
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={handleFetchJobDetails}
                            disabled={isFetchingDetails}
                            className="py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                          >
                            {isFetchingDetails ? 'Fetching...' : 'Fetch Details'}
                          </button>
                        </div>
                        
                        {formErrors.applicationUrl && (
                          <p className="mt-1 text-sm text-red-500">{formErrors.applicationUrl}</p>
                        )}
                        {fetchError && (
                          <p className="mt-1 text-sm text-red-500">{fetchError}</p>
                        )}
                      </div>
                      
                      {/* AI Summary Field */}
                      <div className={`p-4 rounded-md border ${jobFormData.summary ? 'bg-purple-50 border-purple-100' : 'bg-gray-50 border-gray-200'}`}>
                        <h4 className={`text-base font-medium mb-2 ${jobFormData.summary ? 'text-purple-800' : 'text-gray-700'}`}>
                          AI Generated Summary
                        </h4>
                        <textarea
                          id="summary"
                          name="summary"
                          rows={3}
                          value={jobFormData.summary}
                          onChange={handleFormChange}
                          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm"
                          placeholder="Summary will be generated here after fetching details..."
                        />
                        <p className={`mt-1 text-xs ${jobFormData.summary ? 'text-purple-600' : 'text-gray-500'}`}>
                          This summary is automatically generated by AI based on the job description URL.
                        </p>
                      </div>

                      {/* Required Fields Section */}
                      <div className="bg-white p-4 rounded-md border border-gray-200">
                        <div className="grid grid-cols-1 gap-y-4 gap-x-6 sm:grid-cols-6">
                          <div className="sm:col-span-3">
                            <label htmlFor="title" className="block text-sm font-medium text-gray-700">
                              Job Title <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              name="title"
                              id="title"
                              value={jobFormData.title}
                              onChange={handleFormChange}
                              className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm ${
                                formErrors.title ? 'border-red-500' : ''
                              }`}
                              placeholder="e.g., Software Engineer"
                            />
                            {formErrors.title && (
                              <p className="mt-1 text-sm text-red-500">{formErrors.title}</p>
                            )}
                          </div>

                          <div className="sm:col-span-3">
                            <label htmlFor="company-select" className="block text-sm font-medium text-gray-700">
                              Company <span className="text-red-500">*</span>
                            </label>
                            <div className="relative mt-1">
                              <Combobox value={selectedCompany} onChange={handleSelectCompany}>
                                <div className="relative">
                                  <Combobox.Input
                                    id="company-select"
                                    className={`block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm ${
                                      formErrors.company ? 'border-red-500' : ''
                                    }`}
                                    placeholder="Search for company or add new..."
                                    displayValue={(company) => company?.name || ''}
                                    onChange={(event) => {
                                      setCompanySearchQuery(event.target.value);
                                      setJobFormData({
                                        ...jobFormData,
                                        company: event.target.value,
                                        companyId: ''  // Clear companyId when manually typing
                                      });
                                    }}
                                  />
                                  <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                                    <ChevronDownIcon
                                      className="h-5 w-5 text-gray-400"
                                      aria-hidden="true"
                                    />
                                  </Combobox.Button>
                                </div>
                                <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                                  {filteredCompanies.length === 0 && companySearchQuery !== '' ? (
                                    <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                                      No companies found.
                                    </div>
                                  ) : (
                                    filteredCompanies.map((company) => (
                                      <Combobox.Option
                                        key={company.id}
                                        value={company}
                                        className={({ active }) =>
                                          `relative cursor-default select-none py-2 pl-3 pr-9 ${
                                            active ? 'bg-purple-600 text-white' : 'text-gray-900'
                                          }`
                                        }
                                      >
                                        {({ active, selected }) => (
                                          <>
                                            <div className="flex items-center">
                                              <span className={`block truncate font-medium`}>
                                                {company.name}
                                              </span>
                                              {company.industry && (
                                                <span className={`ml-2 text-xs ${active ? 'text-purple-100' : 'text-gray-500'}`}>
                                                  ({company.industry})
                                                </span>
                                              )}
                                            </div>
                                          </>
                                        )}
                                      </Combobox.Option>
                                    ))
                                  )}
                                  <button
                                    type="button"
                                    onClick={() => setIsAddCompanyModalOpen(true)}
                                    className="relative w-full cursor-pointer select-none py-2 pl-3 pr-9 text-left text-purple-600 hover:bg-purple-50 flex items-center gap-1"
                                  >
                                    <PlusCircleIcon className="h-4 w-4" aria-hidden="true" />
                                    <span>Add New Company</span>
                                  </button>
                                </Combobox.Options>
                              </Combobox>
                              {formErrors.company && (
                                <p className="mt-1 text-sm text-red-500">{formErrors.company}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Toggle Job Details Button */}
                      <button
                        type="button"
                        onClick={() => setShowJobDetails(!showJobDetails)}
                        className="w-full flex items-center justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                      >
                        <span>{showJobDetails ? 'Hide Job Details' : 'Show Job Details'}</span>
                        {showJobDetails ? (
                          <ChevronUpIcon className="ml-2 h-4 w-4" aria-hidden="true" />
                        ) : (
                          <ChevronDownIcon className="ml-2 h-4 w-4" aria-hidden="true" />
                        )}
                      </button>
                      
                      {/* Job Details Section - Expandable */}
                      {showJobDetails && (
                        <div className="mt-6">
                          <h4 className="text-base font-medium text-gray-900 mb-4">Additional Job Details</h4>
                          
                          <div className="grid grid-cols-1 gap-y-4 gap-x-6 sm:grid-cols-6">
                            <div className="sm:col-span-3">
                              <label htmlFor="location" className="block text-sm font-medium text-gray-700">
                                Location
                              </label>
                              <input
                                type="text"
                                name="location"
                                id="location"
                                value={jobFormData.location}
                                onChange={handleFormChange}
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm"
                                placeholder="e.g., San Francisco, CA"
                              />
                            </div>

                            <div className="sm:col-span-3">
                              <label htmlFor="industry" className="block text-sm font-medium text-gray-700">
                                Industry
                              </label>
                              <input
                                type="text"
                                name="industry"
                                id="industry"
                                value={jobFormData.industry}
                                onChange={handleFormChange}
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm"
                                placeholder="e.g., Technology, Healthcare"
                              />
                            </div>

                            <div className="sm:col-span-3">
                              <label htmlFor="experienceLevel" className="block text-sm font-medium text-gray-700">
                                Experience Level
                              </label>
                              <select
                                id="experienceLevel"
                                name="experienceLevel"
                                value={jobFormData.experienceLevel}
                                onChange={handleFormChange}
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm"
                              >
                                <option value="">Not Specified</option>
                                <option value="entry">Entry Level</option>
                                <option value="mid">Mid Level</option>
                                <option value="senior">Senior Level</option>
                                <option value="executive">Executive</option>
                              </select>
                            </div>

                            <div className="sm:col-span-3">
                              <label htmlFor="roleType" className="block text-sm font-medium text-gray-700">
                                Role Type
                              </label>
                              <select
                                id="roleType"
                                name="roleType"
                                value={jobFormData.roleType}
                                onChange={handleFormChange}
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm"
                              >
                                <option value="">Not Specified</option>
                                <option value="technical">Technical</option>
                                <option value="non-technical">Non-Technical</option>
                                <option value="management">Management</option>
                                <option value="sales">Sales</option>
                                <option value="marketing">Marketing</option>
                                <option value="design">Design</option>
                                <option value="operations">Operations</option>
                              </select>
                            </div>

                            <div className="sm:col-span-3">
                              <label htmlFor="workEnvironment" className="block text-sm font-medium text-gray-700">
                                Work Environment
                              </label>
                              <textarea
                                id="workEnvironment"
                                name="workEnvironment"
                                rows={2}
                                value={jobFormData.workEnvironment}
                                onChange={handleFormChange}
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm"
                                placeholder="Describe the work environment (e.g., Fast-paced, collaborative, etc.)"
                              />
                            </div>

                            <div className="sm:col-span-3">
                              <label htmlFor="workplace" className="block text-sm font-medium text-gray-700">
                                Workplace Type
                              </label>
                              <select
                                id="workplace"
                                name="workplace"
                                value={jobFormData.workplace}
                                onChange={handleFormChange}
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm"
                              >
                                <option value="remote">Remote</option>
                                <option value="hybrid">Hybrid</option>
                                <option value="on-site">On-site</option>
                              </select>
                            </div>

                            <div className="sm:col-span-3">
                              <label htmlFor="salary" className="block text-sm font-medium text-gray-700">
                                Salary Range
                              </label>
                              <input
                                type="text"
                                name="salary"
                                id="salary"
                                value={jobFormData.salary}
                                onChange={handleFormChange}
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm"
                                placeholder="e.g., $80,000 - $100,000"
                              />
                            </div>

                            <div className="sm:col-span-3">
                              <label htmlFor="postedDate" className="block text-sm font-medium text-gray-700">
                                Posted Date
                              </label>
                              <input
                                type="text"
                                name="postedDate"
                                id="postedDate"
                                value={jobFormData.postedDate}
                                onChange={handleFormChange}
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm"
                                placeholder="e.g., YYYY-MM-DD or '2 days ago'"
                              />
                            </div>

                            <div className="sm:col-span-3">
                              <label htmlFor="applicationDeadline" className="block text-sm font-medium text-gray-700">
                                Application Deadline
                              </label>
                              <input
                                type="text"
                                name="applicationDeadline"
                                id="applicationDeadline"
                                value={jobFormData.applicationDeadline}
                                onChange={handleFormChange}
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm"
                                placeholder="e.g., YYYY-MM-DD"
                              />
                            </div>

                            <div className="sm:col-span-6">
                              <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                                Job Description
                              </label>
                              <textarea
                                id="description"
                                name="description"
                                rows={3}
                                value={jobFormData.description}
                                onChange={handleFormChange}
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm"
                                placeholder="Enter the full job description..."
                              />
                            </div>

                            <div className="sm:col-span-6">
                              <label htmlFor="responsibilities" className="block text-sm font-medium text-gray-700">
                                Responsibilities
                              </label>
                              <textarea
                                id="responsibilities"
                                name="responsibilities"
                                rows={3}
                                value={jobFormData.responsibilities}
                                onChange={handleFormChange}
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm"
                                placeholder="List the job responsibilities..."
                              />
                            </div>

                            <div className="sm:col-span-6">
                              <label htmlFor="requirements" className="block text-sm font-medium text-gray-700">
                                Requirements
                              </label>
                              <textarea
                                id="requirements"
                                name="requirements"
                                rows={3}
                                value={jobFormData.requirements}
                                onChange={handleFormChange}
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm"
                                placeholder="List the job requirements..."
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    <div className="mt-8 flex justify-end space-x-3 border-t pt-6">
                      <button
                        type="button"
                        className="py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                        onClick={onClose}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                        onClick={handleAddJob}
                      >
                        Save Job
                      </button>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
      
      {/* Add Company Modal */}
      <AddCompanyModal 
        isOpen={isAddCompanyModalOpen}
        onClose={() => setIsAddCompanyModalOpen(false)}
        onCompanyAdded={handleAddNewCompany}
        userId={currentUser?.uid}
      />
    </>
  );
};

export default AddJobModal;